import { logger } from '@automata/utils';
import {
  PlanningTargetsInput,
  ReferenceDataInput,
  WorkcellInstrumentInput,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';
import { createSelector } from '@reduxjs/toolkit';
import mapValues from 'lodash/mapValues';
import merge from 'lodash/merge';
import reduce from 'lodash/reduce';

import {
  PREVIEW_FAKE_LABWARE_DEF_URI,
  previewFakeLabwareDefinition,
} from './constants';
import deriveWorkflowJson, {
  deriveWorkflowJsonForSave,
} from './deriveWorkflowJson';
import { getFieldErrorsForTasks } from './getFieldErrorsForTasks';
import getJsonLabwareTypes, {
  getJsonLabwareTypesForSave,
} from './getJsonLabwareTypes';
import { getJsonPlanningTargetsTimingTargets } from './getJsonPlanningTargets';
import {
  getJsonTaskDependencies,
  getJsonTaskDependenciesForSave,
} from './getJsonTaskDependencies';
import {
  getJsonTimeConstraintsForSave,
  getJsonTimeConstraintsPreview,
  getJsonTimeConstraintsStrict,
} from './getJsonTimeConstraints';
import getTimeEstimateValues from './getTimeEstimateValues';
import { isMagicallyPopulatedDataInputKey } from './isMagicallyPopulatedDataInputKey';
import labwareToJson, { labwareToJsonForSave } from './labwareToJson';
import tasksToJson from './tasksToJson';
import { walkTaskListForValidationForSave } from './tasksToJson/walkTaskListForValidation';
import { BACKGROUND_DRIVER_NAMES } from '../../constants';
import { getAllLabwareDefinitions as getAllLabwareDefinitionsStrict } from '../../labware';
import { LabwareDefByDefURI } from '../../labware/types';
import {
  AllTaskProperties,
  LabwareProperties,
  PropertiesType,
} from '../../types';
import { ExtendedTaskOutputReferenceInput } from '../../types/taskNode';
import {
  selectDataDependencyEdges,
  selectGraphHasCycle,
  selectSlice as selectGraphState,
  selectGraphologyGraphWithLabwareDependencies,
  selectLabwareDependencyEdges,
  selectTaskDependencies,
  selectTimeConstraintInstances,
} from '../graph/selectors';
import {
  selectCodeTaskDataHandlesByTaskID,
  selectConnectedLabwareIds,
  selectConnectedLabwareProperties,
  selectDataKeyByHandleID,
  selectTaskDependenciesErrors,
  selectTimeConstraintErrorsById,
  selectTimeConstraintLabwareCorrespondencesById,
} from '../selectors';
import { selectFinalTasksByLabwareId } from '../selectors/selectFinalTasksByLabwareId';
import { selectLabwareAndSourceTaskByTaskId } from '../selectors/selectLabwareAndSourceNodeByTaskId';
import { selectLabwareIdsForTaskByTaskId } from '../selectors/selectLabwareIdsForTaskByTaskId';
import {
  selectFinalLocationSlotConflicts,
  selectSlotCollisionConflicts,
  selectStartLocationSlotConflicts,
  selectStartLocationSlotInvalidInputs,
} from '../selectors/slotConflict';
import type { LabwareByHandleIndexByTaskId } from '../selectors/types';
import * as LabwareErrors from '../shared/labwareErrors';
import type {
  DataDependenciesByTaskId,
  LabwareSerializationError,
} from '../shared/types';
import {
  selectActionTaskPropertiesStrict,
  selectDriverActionConfigs,
  selectDriverForTaskByTaskId,
  selectExecutorOptions,
  selectExecutorOptionsForSave,
  selectInstrumentDataById,
  selectLabwareOffsetDefinitions,
  selectLabwareProperties as selectLabwarePropertiesStrict,
  selectParameterDefinitions,
  selectParameterValues,
  selectPlannerOptions,
  selectPlannerOptionsForSave,
  selectSchedulerVersion,
  selectSelectedWorkcellId,
  selectTaskPropertiesPreview,
  selectTaskPropertiesStrict,
  selectVisualWorkflow,
  selectWorkcellInstruments,
  selectWorkcellTransportConfigId,
  selectWorkcellTransportConfigType,
  selectWorkcellTransportConfigVersion,
  selectWorkflowAuthor,
  selectWorkflowDescription,
  selectWorkflowName,
} from '../workflow/selectors';

export * from '../shared/labwareErrors';
export * from './taskErrors';
export * from '../shared/types';

/** The workflow config JSON expects driver names to have version suffixes like _v1 _v2 etc. */
function getWorkflowDriverString({
  driverName,
  driverVersion,
}: {
  driverName: string;
  driverVersion: string;
}): string {
  return `${driverName}_${driverVersion}`;
}

export const _selectJsonInstruments = createSelector(
  selectInstrumentDataById,
  selectActionTaskPropertiesStrict,
  selectLabwarePropertiesStrict,
  (instruments, tasks, labware): WorkcellInstrumentInput[] => {
    // const errors: WorkflowSerializationError[] = [];
    const validInstruments: WorkcellInstrumentInput[] = [];
    const usedInstruments = new Set(
      [
        ...Object.values(tasks).map((task) => task.instrumentId),
        ...Object.values(labware).map((labware) => labware.startLocation),
      ].filter((x): x is NonNullable<typeof x> => x != null)
    );

    for (const instrument of Object.values(instruments)) {
      // we only include instruments that are actually used in the workflow
      if (
        usedInstruments.has(instrument.id) ||
        BACKGROUND_DRIVER_NAMES.includes(instrument.driverName)
      ) {
        const {
          id,
          bench,
          capacity,
          config,
          driverName,
          driverVersion,
          name,
          mappings,
          is_slotless_container,
          simulate,
        } = instrument;
        validInstruments.push({
          // NOTE(IL, 2024-04-09): some non-required properties are not set here, eg `mappings`
          id,
          name,
          // TODO(IL, 2024-05-14): consider using driver rather than instrument UUID for the type, to support load balancing
          type: id,
          capacity,
          config,
          mappings: mappings || [],
          driver: getWorkflowDriverString({ driverName, driverVersion }),
          bench,
          is_slotless_container,
          simulate,
        });
      }
    }

    // if (errors.length > 0) {
    //   return { workflowSerializationErrors: errors };
    // }

    return validInstruments;
  }
);

/** Autofill missing fields with fake data for preview */
export const _selectLabwarePropertiesPreview = createSelector(
  selectConnectedLabwareIds,
  selectLabwarePropertiesStrict,
  (
    labwareIds: string[],
    labware: Record<string, LabwareProperties>
  ): Record<string, LabwareProperties> => {
    return labwareIds.reduce<typeof labware>((acc, labwareId) => {
      const labwareProperties = labware[labwareId];
      if (!labwareProperties) return acc;

      const labwareDefinitionURI = labwareProperties.labwareDefinitionURI;
      if (labwareDefinitionURI == null) {
        return {
          ...acc,
          [labwareId]: {
            ...labwareProperties,
            labwareDefinitionURI: PREVIEW_FAKE_LABWARE_DEF_URI,
          },
        };
      }
      return { ...acc, [labwareId]: labwareProperties };
    }, {});
  }
);

const _preview_labware = {
  ...getAllLabwareDefinitionsStrict(),
  [PREVIEW_FAKE_LABWARE_DEF_URI]: previewFakeLabwareDefinition,
};

export function _getAllLabwareDefinitionsPreview(): LabwareDefByDefURI {
  return _preview_labware;
}

export function _getAllLabwareDefinitionsStrict(): LabwareDefByDefURI {
  return getAllLabwareDefinitionsStrict();
}

export const _selectJsonLabwarePreview = createSelector(
  _getAllLabwareDefinitionsPreview,
  _selectLabwarePropertiesPreview,
  selectInstrumentDataById,
  labwareToJson
);

export const _selectJsonLabwareStrict = createSelector(
  _getAllLabwareDefinitionsStrict,
  selectConnectedLabwareProperties,
  selectInstrumentDataById,
  labwareToJson
);

export const _selectJsonLabwareForSave = createSelector(
  selectConnectedLabwareProperties,
  selectInstrumentDataById,
  labwareToJsonForSave
);

export const _selectJsonLabwareTypesPreview = createSelector(
  _getAllLabwareDefinitionsPreview,
  _selectLabwarePropertiesPreview,
  selectLabwareOffsetDefinitions,
  getJsonLabwareTypes
);

export const _selectJsonLabwareTypesStrict = createSelector(
  _getAllLabwareDefinitionsStrict,
  selectLabwarePropertiesStrict,
  selectLabwareOffsetDefinitions,
  getJsonLabwareTypes
);

export const _selectJsonLabwareTypesForSave = createSelector(
  _getAllLabwareDefinitionsStrict,
  selectLabwarePropertiesStrict,
  selectLabwareOffsetDefinitions,
  getJsonLabwareTypesForSave
);

export const _selectJsonTimeConstraintsStrict = createSelector(
  selectTimeConstraintInstances,
  getJsonTimeConstraintsStrict
);

export const _selectJsonTimeConstraintsForSave = createSelector(
  selectTimeConstraintInstances,
  getJsonTimeConstraintsForSave
);

export const _selectJsonTimeConstraintsPreview = createSelector(
  selectTimeConstraintInstances,
  selectTimeConstraintLabwareCorrespondencesById,
  selectTimeConstraintErrorsById,
  getJsonTimeConstraintsPreview
);

export const _selectTimeEstimateValuesByTaskId = createSelector(
  selectDriverForTaskByTaskId,
  selectActionTaskPropertiesStrict,
  selectParameterDefinitions,
  getTimeEstimateValues
);

export const getSortedTaskProperties = (
  taskPropertiesById: Record<string, AllTaskProperties>
): AllTaskProperties[] => {
  return Object.values(taskPropertiesById).sort((a, b) => {
    // should be processed first
    const containerTypes = [
      PropertiesType.Conditional,
      PropertiesType.ContinuousLoading,
    ];

    const aPriority = containerTypes.indexOf(a.propertiesType);
    const bPriority = containerTypes.indexOf(b.propertiesType);

    if (aPriority !== -1 && bPriority !== -1) return aPriority - bPriority;
    if (aPriority !== -1) return -1;
    if (bPriority !== -1) return 1;

    return 0;
  });
};

export const _selectTaskIds = createSelector(
  selectTaskPropertiesStrict,
  selectLabwareIdsForTaskByTaskId,
  (
    taskProperties: Record<string, AllTaskProperties>,
    labwareIdsForTaskByTaskId: LabwareByHandleIndexByTaskId
  ): string[] => {
    // Sort so that conditionals are processed first to ensure correct handling of child tasks
    // See tasksToJson/walkTaskListForValidation.ts `childTaskIds` and `hoistedTaskIds`
    const taskPropertiesArray = getSortedTaskProperties(taskProperties);

    return reduce<AllTaskProperties, string[]>(
      taskPropertiesArray,
      (acc, taskProperty) => {
        const isDataNode =
          taskProperty.propertiesType === PropertiesType.Code ||
          taskProperty.propertiesType === PropertiesType.DataExport;
        const isLabwareConnected =
          Object.values(labwareIdsForTaskByTaskId[taskProperty.id] ?? {})
            .length > 0;

        const shouldIncludeTask =
          isDataNode || isLabwareConnected || taskProperty.isLabwareLessTask;

        if (shouldIncludeTask) {
          return [...acc, taskProperty.id];
        }

        return acc;
      },
      []
    );
  }
);

export const _selectFieldErrorsForTasks = createSelector(
  _selectTaskIds,
  selectDriverForTaskByTaskId,
  selectTaskPropertiesStrict,
  selectParameterDefinitions,
  getFieldErrorsForTasks
);

export const selectFieldErrorsForTasksUsingParams = createSelector(
  _selectTaskIds,
  selectDriverForTaskByTaskId,
  selectTaskPropertiesStrict,
  selectParameterValues,
  getFieldErrorsForTasks
);

const _selectTaskDependencies = createSelector(
  selectTaskDependencies,
  selectTaskDependenciesErrors,
  getJsonTaskDependencies
);

const _selectMagicDataDependencies = createSelector(
  selectCodeTaskDataHandlesByTaskID,
  (dataHandlesByTaskID) => {
    return mapValues(dataHandlesByTaskID, (dataHandles) =>
      dataHandles
        .filter(
          (dataHandle) =>
            dataHandle.handleType === 'DATA_INPUT' &&
            isMagicallyPopulatedDataInputKey(dataHandle.dataKey)
        )
        .reduce<{ [dataKey: string]: ReferenceDataInput }>(
          (acc, { dataKey }) => ({ ...acc, [dataKey]: { key: dataKey } }),
          {}
        )
    );
  }
);

export const selectDataDependencies = createSelector(
  selectDataDependencyEdges,
  selectDataKeyByHandleID,
  _selectMagicDataDependencies,
  (
    dataDependencyEdges,
    dataKeyByHandleID,
    magicDataDependencies
  ): DataDependenciesByTaskId => {
    const dataDependenciesFromEdges =
      dataDependencyEdges.reduce<DataDependenciesByTaskId>((result, edge) => {
        const { target: taskId, source: sourceTask } = edge;
        const taskInput = dataKeyByHandleID[edge.targetHandle ?? '']?.dataKey;
        const outputName = dataKeyByHandleID[edge.sourceHandle ?? '']?.dataKey;
        const outputType = dataKeyByHandleID[edge.sourceHandle ?? '']?.type;

        if (taskId && taskInput) {
          result[taskId] = {
            ...result[taskId],
            [taskInput]: {
              task_id: sourceTask,
              output_name: outputName,
              type: outputType,
            } satisfies ExtendedTaskOutputReferenceInput,
          };
        }

        return result;
      }, {});

    return merge({}, dataDependenciesFromEdges, magicDataDependencies);
  }
);

export const selectSlotConflictErrorsStrict = createSelector(
  selectStartLocationSlotConflicts,
  selectFinalLocationSlotConflicts,
  selectStartLocationSlotInvalidInputs,
  selectSlotCollisionConflicts,
  selectFinalTasksByLabwareId,
  (
    startSlotConflicts,
    finalSlotConflicts,
    startInvalidInputs,
    slotCollisionConflicts,
    finalTasksByLabwareId
  ): LabwareSerializationError[] => {
    const errors: LabwareSerializationError[] = [
      ...startInvalidInputs,
      ...slotCollisionConflicts,
    ];

    for (const {
      labwareId,
      instrumentId,
      capacity,
    } of startSlotConflicts.labwareWithOverflows) {
      errors.push({
        errorType: 'labware',
        message: LabwareErrors.LABWARE_START_LOCATION_SLOT_CAPACITY_EXCEEDED,
        labwareId,
        instrumentId,
        capacity,
      });
    }

    for (const {
      labwareId,
      instrumentId,
    } of startSlotConflicts.labwareWithSelfCollisions) {
      errors.push({
        errorType: 'labware',
        message: LabwareErrors.LABWARE_START_LOCATION_SELF_COLLISION,
        labwareId,
        instrumentId,
      });
    }

    for (const {
      labwareId,
      instrumentId,
      slotsInUse,
    } of startSlotConflicts.labwareWithSlotCollisions) {
      errors.push({
        errorType: 'labware',
        message: LabwareErrors.LABWARE_START_LOCATION_SLOT_COLLISION,
        labwareId,
        instrumentId,
        slotsInUse,
      });
    }

    for (const {
      labwareId,
      instrumentId,
      capacity,
    } of finalSlotConflicts.labwareWithOverflows) {
      const taskId = finalTasksByLabwareId[labwareId] ?? '???';
      if (finalTasksByLabwareId[labwareId] === undefined)
        logger.warn(
          `Slot conflict final location error for labware ${labwareId}, could not find final task for this labware`
        );
      errors.push({
        errorType: 'labware',
        message: LabwareErrors.LABWARE_FINAL_LOCATION_SLOT_CAPACITY_EXCEEDED,
        labwareId,
        instrumentId,
        taskId,
        capacity,
      });
    }

    for (const {
      labwareId,
      instrumentId,
    } of finalSlotConflicts.labwareWithSelfCollisions) {
      const taskId = finalTasksByLabwareId[labwareId] ?? '???';
      if (finalTasksByLabwareId[labwareId] === undefined)
        logger.warn(
          `Slot conflict final location error for labware ${labwareId}, could not find final task for this labware`
        );
      errors.push({
        errorType: 'labware',
        message: LabwareErrors.LABWARE_FINAL_LOCATION_SELF_COLLISION,
        labwareId,
        instrumentId,
        taskId,
      });
    }

    for (const {
      labwareId,
      instrumentId,
      slotsInUse,
    } of finalSlotConflicts.labwareWithSlotCollisions) {
      const taskId = finalTasksByLabwareId[labwareId] ?? '???';
      if (finalTasksByLabwareId[labwareId] === undefined)
        logger.warn(
          `Slot conflict final location error for labware ${labwareId}, could not find final task for this labware`
        );
      errors.push({
        errorType: 'labware',
        message: LabwareErrors.LABWARE_FINAL_LOCATION_SLOT_COLLISION,
        labwareId,
        instrumentId,
        taskId,
        slotsInUse,
      });
    }

    return errors;
  }
);

const _selectTaskDependenciesForSave = createSelector(
  selectTaskDependencies,
  selectTaskDependenciesErrors,
  getJsonTaskDependenciesForSave
);

export const _selectJsonTasksPreview = createSelector(
  _selectTaskIds,
  _selectJsonLabwarePreview,
  selectLabwareIdsForTaskByTaskId,
  _selectFieldErrorsForTasks,
  selectTaskPropertiesPreview,
  selectGraphHasCycle,
  selectGraphologyGraphWithLabwareDependencies,
  _selectTimeEstimateValuesByTaskId,
  _selectTaskDependencies,
  selectDataDependencies,
  selectInstrumentDataById,
  selectDriverActionConfigs,
  selectLabwareAndSourceTaskByTaskId,
  selectLabwareDependencyEdges,
  tasksToJson
);

export const _selectJsonTasksStrict = createSelector(
  _selectTaskIds,
  _selectJsonLabwareStrict,
  selectLabwareIdsForTaskByTaskId,
  _selectFieldErrorsForTasks,
  selectTaskPropertiesStrict,
  selectGraphHasCycle,
  selectGraphologyGraphWithLabwareDependencies,
  _selectTimeEstimateValuesByTaskId,
  _selectTaskDependencies,
  selectDataDependencies,
  selectInstrumentDataById,
  selectDriverActionConfigs,
  selectLabwareAndSourceTaskByTaskId,
  selectLabwareDependencyEdges,
  tasksToJson
);

export const _selectJsonTasksForSave = createSelector(
  _selectTaskIds,
  selectGraphologyGraphWithLabwareDependencies,
  selectLabwareIdsForTaskByTaskId,
  selectTaskPropertiesStrict,
  _selectTimeEstimateValuesByTaskId,
  _selectTaskDependenciesForSave,
  selectDataDependencies,
  selectInstrumentDataById,
  selectDriverActionConfigs,
  selectLabwareAndSourceTaskByTaskId,
  selectLabwareDependencyEdges,
  walkTaskListForValidationForSave
);

const _selectPlanningTargets = createSelector(
  selectTimeConstraintInstances,
  (tcs): PlanningTargetsInput => {
    const planningTargets = getJsonPlanningTargetsTimingTargets(tcs);
    const consistency = planningTargets.consistency;

    return {
      timing_targets: planningTargets.timing_targets,
      consistency:
        consistency == null
          ? undefined
          : {
              default: consistency,
            },
    };
  }
);

export const selectWorkflowJsonToPreview = createSelector(
  selectWorkflowAuthor,
  selectWorkflowDescription,
  selectWorkflowName,
  selectParameterDefinitions,
  selectPlannerOptions,
  selectExecutorOptions,
  _selectJsonInstruments,
  _selectJsonLabwarePreview,
  _selectJsonLabwareTypesPreview,
  _selectJsonTasksPreview,
  _selectJsonTimeConstraintsPreview,
  selectSlotConflictErrorsStrict,
  selectSchedulerVersion,
  selectSelectedWorkcellId,
  _selectPlanningTargets,
  (
    author,
    description,
    name,
    params,
    planner,
    executor,
    inst,
    lab,
    labTypes,
    tasks,
    tcs,
    errors,
    schedVer,
    selectedWorkcellId,
    planningTargets
  ) =>
    deriveWorkflowJson(
      author,
      description,
      name,
      params,
      planner,
      executor,
      inst,
      lab,
      labTypes,
      tasks,
      tcs,
      errors,
      schedVer,
      selectedWorkcellId,
      planningTargets
    )
);

export const selectWorkflowJsonStrict = createSelector(
  selectWorkflowAuthor,
  selectWorkflowDescription,
  selectWorkflowName,
  selectParameterDefinitions,
  selectPlannerOptions,
  selectExecutorOptions,
  _selectJsonInstruments,
  _selectJsonLabwareStrict,
  _selectJsonLabwareTypesStrict,
  _selectJsonTasksStrict,
  _selectJsonTimeConstraintsStrict,
  selectSlotConflictErrorsStrict,
  selectSchedulerVersion,
  selectSelectedWorkcellId,
  _selectPlanningTargets,
  (
    author,
    description,
    name,
    params,
    planner,
    executor,
    inst,
    lab,
    labTypes,
    tasks,
    tcs,
    errors,
    schedVer,
    selectedWorkcellId,
    planningTargets
  ) =>
    deriveWorkflowJson(
      author,
      description,
      name,
      params,
      planner,
      executor,
      inst,
      lab,
      labTypes,
      tasks,
      tcs,
      errors,
      schedVer,
      selectedWorkcellId,
      planningTargets
    )
);

export const selectWorkflowJsonToSave = createSelector(
  selectWorkflowAuthor,
  selectWorkflowDescription,
  selectWorkflowName,
  selectParameterDefinitions,
  selectPlannerOptionsForSave,
  selectExecutorOptionsForSave,
  _selectJsonInstruments,
  _selectJsonLabwareForSave,
  _selectJsonLabwareTypesForSave,
  _selectJsonTasksForSave,
  selectGraphState,
  selectVisualWorkflow as any, // fixme
  _selectJsonTimeConstraintsForSave,
  selectWorkcellInstruments,
  selectWorkcellTransportConfigId,
  selectWorkcellTransportConfigType,
  selectWorkcellTransportConfigVersion,
  selectSelectedWorkcellId,
  selectSchedulerVersion,
  _selectPlanningTargets,
  (
    author,
    description,
    name,
    params,
    planner,
    executor,
    inst,
    lab,
    labTypes,
    tasks,
    graph,
    visualWorkflow,
    timeConstraints,
    workcellInstruments,
    workcellTransportConfigId,
    workcellTransportConfigType,
    workcellTransportConfigVersion,
    selectedWorkcellId,
    schedulerVersion,
    planningTargets
  ) =>
    deriveWorkflowJsonForSave(
      author,
      description,
      name,
      params,
      planner,
      executor,
      inst,
      lab,
      labTypes,
      tasks,
      graph,
      visualWorkflow,
      timeConstraints,
      workcellInstruments,
      workcellTransportConfigId,
      workcellTransportConfigType,
      workcellTransportConfigVersion,
      selectedWorkcellId,
      schedulerVersion,
      planningTargets
    )
);
